import React, { useMemo, useState } from 'react';
import { BLOCKS } from '@contentful/rich-text-types';
import { navigate } from 'gatsby';
import { useIntl } from 'react-intl';

import { CardCTA, CombinatorSectionItem, PageSection } from '../../ContentfulTypes';
import RichText from '../UI/RichText';
import Image, { ImageLoadingType } from '../UI/Image/Image';
import ButtonLink from '../UI/ButtonLink/ButtonLink';
import extractArrayData from '../../utils/extractArrayData';
import { Contentful } from '../../types';

import {
  StoreLocationSection,
  CTAButton,
  SectionHeading,
  InputField,
  Icon,
  InputFieldWrapper,
  LogoWrapper,
  LogoDiv,
  CtaContainer,
} from './styles';

const heading = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <SectionHeading>{children}</SectionHeading>
    ),
  },
};

interface Props {
  data: PageSection[],
  showSearch?: boolean;
  id?: string;
  imageLoadingType: ImageLoadingType;
}
const StoreLocationSearch = ({ data, showSearch = true, id, imageLoadingType }: Props) => {
  const { locale } = useIntl();
  const sectionHeading = useMemo(() => data.find(
    ({ __typename }) => __typename === 'ContentfulTextSection',
  )?.heading, [data]);

  const backgroundImageDam = useMemo(() => extractArrayData<PageSection>(data, Contentful.TypeName, 'ContentfulImageSection'), [data]);

  const formFields = useMemo(() => extractArrayData<PageSection>(data, Contentful.TypeName, 'ContentfulForm')?.formFields, [data]);
  const searchBarLabel = formFields?.[0]?.displayLabel;
  const searchButton = formFields?.[1]?.displayLabel;

  const storeLogos = useMemo(() => (extractArrayData<PageSection>(data, Contentful.TypeName, 'ContentfulStoreLogosSection') as (CombinatorSectionItem | undefined))?.tmsStores, [data]);

  const cta = useMemo(() => extractArrayData<PageSection>(data, Contentful.TypeName, 'ContentfulCta') as (CardCTA | undefined), [data]);

  // Filtering store logos based on selected province
  // const provinceStoreLogos = [];
  // storeLogos.every((item) => {
  //   item.storeRegion.every((region) => {
  //     if (region.toLowerCase() === preferredProvince.toLowerCase()) {
  //       provinceStoreLogos.push(item);
  //       return false;
  //     }
  //     // eslint-disable-next-line
  //     else {
  //       return true;
  //     }
  //   });
  //   return true;
  // });

  // Handling the address entered by user manually
  const [addressQuery, setAddressQuery] = useState('');

  const handleAddress = (e) => {
    setAddressQuery(e.currentTarget.value);
  };

  const submit = (e) => {
    e.preventDefault();
    navigate(`/${locale}/locations`, {
      state: { addressQuery },
    });
  };

  return (
    <StoreLocationSection
      data-testid="store-location-section"
      desktopImage={
        backgroundImageDam?.backgroundImageDesktop?.asset?.localImage?.publicURL
      }
      tabletImage={
        backgroundImageDam?.backgroundImageTablet?.asset?.localImage?.publicURL
      }
      mobileImage={
        backgroundImageDam?.backgroundImageMobile?.asset?.localImage?.publicURL
      }
      showSearch={showSearch}
      id={id}
    >
      <RichText document={sectionHeading} options={heading} />
      {showSearch
        && (
        <form onSubmit={submit}>
          <InputFieldWrapper>
            <InputField
              type="text"
              id="address-input"
              name="address-input"
              placeholder={searchBarLabel}
              aria-label="search a location"
              onChange={(e) => handleAddress(e)}
              role="search"
            />
            <CTAButton type="submit">{searchButton}</CTAButton>
          </InputFieldWrapper>
        </form>
        )}
      <LogoWrapper>
        {storeLogos?.map(({ storeId, logo }) => logo ? (
          <LogoDiv key={storeId}>
            <Icon>
              <Image image={logo} loading={imageLoadingType} />
            </Icon>
          </LogoDiv>
        ) : null)}
      </LogoWrapper>
      {cta
        && (
          <CtaContainer>
            <ButtonLink
              buttonText={cta?.ctaLabel?.ctaLabel}
              buttonColor={cta?.ctaColor}
              buttonSize="medium"
              url={cta?.ctaUrl}
              route={cta?.ctaTarget}
              locale={locale}
            />
          </CtaContainer>
        )}
    </StoreLocationSection>
  );
};

// ts-prune-ignore-next
export default StoreLocationSearch;
